import React from "react";
import { DropzoneOptions, DropzoneState } from "react-dropzone";
import { FormProvider, useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { MIME_TYPE, MULTIPART_UPLOAD_TYPE } from "common/constants";
import { useImportCoastalAGeometryMutation } from "../../../../../generated/graphql";
import { useMultipartFileUpload } from "../../../../../hooks/useMultipartFileUpload";
import { useStatusToasts } from "../../../../../hooks/useStatusToasts";
import {
  ButtonSection,
  Container,
  ContentSection,
  FormSection,
  HeaderSection,
  PrimaryButtons,
} from "../../../../Common/__styles__/Modal";
import { InputRow } from "../../../../Common/__styles__/Modal";
import { Button } from "../../../../Common/Button";
import Modal from "../../../../Common/Modal";
import { Body, Title } from "../../../../Common/Typography";
import { Checkbox, SingleFileUpload } from "../../../../Inputs/react-hook-form";

type CoastalAImportFormType = {
  file: {
    blob: any;
    originalFilename: string;
  };
  deleteExisting: boolean;
};

type CoastalAImportModalProps = {
  firmId: string;
  onClose: () => void;
  useDropzone?: (args: DropzoneOptions) => DropzoneState;
};

export const useCoastalAImportModal = ({ firmId }: { firmId: string }) => {
  const [showCoastalAImportModal, hideCoastalAImportModal] = useModal(() => (
    <Modal onRequestClose={hideCoastalAImportModal}>
      <CoastalAImportForm firmId={firmId} onClose={hideCoastalAImportModal} />
    </Modal>
  ));

  return [showCoastalAImportModal, hideCoastalAImportModal] as const;
};

export const CoastalAImportForm = ({
  firmId,
  onClose,
  useDropzone,
}: CoastalAImportModalProps) => {
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const [importCoastalA] = useImportCoastalAGeometryMutation({
    onCompleted: () => {
      addSuccessToast("Coastal A geometry import has been scheduled");
      onClose();
    },
    onError: () => {
      addErrorToast("Failed to schedule Coastal A geometry import");
    },
  });

  const [uploadFile, { loading: isUploading }] = useMultipartFileUpload({
    onError: () => addErrorToast("Failed to upload file"),
  });

  const formMethods = useForm<CoastalAImportFormType>({
    defaultValues: {
      deleteExisting: true,
    },
    mode: "onChange",
  });
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = formMethods;

  const onSubmit = handleSubmit(async data => {
    uploadFile({
      file: data.file.blob,
      uploadType: MULTIPART_UPLOAD_TYPE.SCRIPT_JOB,
      onCompleted: async key => {
        await importCoastalA({
          variables: {
            data: {
              firmId,
              file: key,
              deleteExisting: data.deleteExisting,
            },
          },
        });
      },
    });
  });

  return (
    <FormProvider {...formMethods}>
      <Container width="narrow">
        <HeaderSection>
          <Title size="large" type="semiBold">
            Import Coastal A Geometries
          </Title>
          <Body type="regular" size="default">
            Upload a GeoJSON file containing coastal A geometries, with one
            feature per geometry. Additional feature properties will be ignored.
          </Body>
        </HeaderSection>
        <FormSection>
          <ContentSection>
            <SingleFileUpload
              control={control}
              name="file"
              label="Coastal A"
              allowedMimeTypes={[MIME_TYPE.GEOJSON]}
              compact={true}
              hasImage={false}
              key="file"
              useDropzone={useDropzone}
              required
              rules={{ required: true }}
            />
            <InputRow />
            <Checkbox
              name="deleteExisting"
              control={control}
              label="Delete existing Coastal A geometries"
            />
          </ContentSection>
        </FormSection>
        <ButtonSection>
          <PrimaryButtons>
            <Button styleVariant="secondary" onClick={onClose} size="medium">
              Cancel
            </Button>
            <Button
              styleVariant="primary"
              onClick={onSubmit}
              size="medium"
              disabled={!isValid || isUploading}
            >
              Import
            </Button>
          </PrimaryButtons>
        </ButtonSection>
      </Container>
    </FormProvider>
  );
};

export default CoastalAImportForm;
